import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from 'api';
import { WEEKLY_CREDIT_ALLOWANCE } from 'utils/pricing';
import { useCreditPricing } from 'hooks/useCreditPricing';

export const CREDITS_QUERY_KEY = ['credits'];

/**
 * Default credit costs, used as a fallback when the API doesn't return a cost
 * for a given action. Keys match the `action` field on the API's action list.
 */
export const ACTION_CREDIT_COSTS: Record<string, number> = {
  JD_UPLOAD_EXISTING: 3,
  JD_GENERATE_AI: 5,
  CV_ANALYSIS: 5,
  GENERATE_QUESTIONS: 8,
  AI_REFINE_QUESTION: 3,
  ASSESSMENT_SCORING: 8,
  CV_RELEVANCE: 4,
  STRENGTHS_REPORT: 15,
  COMPARISON_RUN: 8,
  SHARE_REPORT: 3,
  ROLE_ANALYTICS: 5,
  COLLAB_SESSION: 3,
};

export interface CreditAction {
  action: string;
  action_name: string;
  credits_per_use: number;
  can_afford: boolean;
  uses_remaining: number;
}

export interface CreditBucket {
  /** Credits currently spendable from this bucket. */
  balance: number;
  /**
   * When the bucket next resets (weekly) or expires (purchased).
   * `null` whenever the server hasn't told us — never guessed client-side,
   * because a wrong date here is worse than no date.
   */
  date: string | null;
  /** Whole days until `date`, or `null` when `date` is unknown. */
  daysRemaining: number | null;
}

export interface CreditsSnapshot {
  /** Free allowance that refreshes every week. Does not roll over. */
  weekly: CreditBucket & { allowance: number };
  /** Topped-up credits, valid for a fixed window from purchase. */
  purchased: CreditBucket;
  /**
   * Credits carried over from a legacy subscription that is being honored
   * until it expires. Zero for every account on the credit model, so the UI
   * only surfaces this bucket when there is something in it. Labelling these
   * as "purchased" would misreport what the account actually bought.
   */
  legacy: number;
  /** True while a grandfathered subscription is still being honored. */
  isGrandfathered: boolean;
  /** The server's own explanation of the legacy arrangement, if any. */
  legacyNote: string | null;
  /** Everything spendable right now — weekly plus purchased plus legacy. */
  totalAvailable: number;
  /**
   * The most the account could hold right now: a full weekly allowance plus the
   * purchased balance. Gauges divide `totalAvailable` by this so an account with
   * topped-up credits never reads as empty just because its weekly grant is spent.
   */
  spendableCeiling: number;
  /** Credits consumed out of this week's allowance. */
  usedThisPeriod: number;
  runningLow: boolean;
  isExhausted: boolean;
  actions: CreditAction[];
}

const EMPTY_SNAPSHOT: CreditsSnapshot = {
  weekly: { balance: 0, allowance: WEEKLY_CREDIT_ALLOWANCE, date: null, daysRemaining: null },
  purchased: { balance: 0, date: null, daysRemaining: null },
  legacy: 0,
  isGrandfathered: false,
  legacyNote: null,
  totalAvailable: 0,
  spendableCeiling: WEEKLY_CREDIT_ALLOWANCE,
  usedThisPeriod: 0,
  runningLow: false,
  isExhausted: false,
  actions: [],
};

const toNumber = (value: unknown): number | null => {
  const parsed = typeof value === 'string' ? Number(value) : value;
  return typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : null;
};

/** Whole days from now until `date`, floored at 0. `null` for missing/invalid dates. */
const daysUntil = (date: string | null): number | null => {
  if (!date) return null;
  const target = new Date(date).getTime();
  if (Number.isNaN(target)) return null;
  const diffMs = target - Date.now();
  return Math.max(0, Math.ceil(diffMs / (24 * 60 * 60 * 1000)));
};

const asDateString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() !== '' && !Number.isNaN(new Date(value).getTime())
    ? value
    : null;

/**
 * Map the billing payload onto the credit model the UI speaks.
 *
 * The backend migration to a pure credit system is still in flight, so this
 * reads the new weekly/purchased shape when present and falls back to the
 * legacy subscription-era fields otherwise. When the backend lands, the
 * fallback branches are the only thing that needs deleting — no consumer of
 * `useCredits` changes.
 *
 * New shape (preferred):
 *   credits: {
 *     weekly:    { balance, allowance, resets_on, days_until_reset },
 *     purchased: { balance, expires_on, days_until_expiry },
 *     total_available, used_this_period, running_low
 *   }
 *
 * Legacy shape (still what production returns): credits.{ total_available,
 *   allocated_this_period, used_this_period, running_low,
 *   breakdown: { subscription, rollover, purchased, bonus } }
 *
 * In the legacy shape `breakdown.rollover` holds credits carried over from a
 * grandfathered subscription. Those were not bought, so they get their own
 * bucket rather than inflating "purchased" — see `legacy`.
 */
const normalizeCredits = (raw: any, weeklyGrant: number): CreditsSnapshot => {
  const credits = raw?.credits;
  if (!credits) return EMPTY_SNAPSHOT;

  const weeklyRaw = credits.weekly ?? credits.free ?? null;
  const purchasedRaw = credits.purchased_credits ?? credits.purchased ?? null;
  const breakdown = credits.breakdown ?? {};

  // Only an explicit weekly figure from the server can override the standard
  // grant. Legacy fields like `allocated_this_period` carry a *plan's* monthly
  // allocation (e.g. 1200), which would render a nonsense "0/1,200 weekly".
  const allowance =
    [toNumber(weeklyRaw?.limit), toNumber(weeklyRaw?.allowance), toNumber(weeklyRaw?.total)].find(
      (value): value is number => value !== null && value > 0
    ) ?? weeklyGrant;

  const totalAvailable = Math.max(0, toNumber(credits.total_available) ?? 0);

  const isGrandfathered = raw?.subscription?.grandfathered === true;

  // Credits from a legacy subscription being honored. Spendable, but not bought —
  // reporting them as "purchased" would misstate what the account paid for.
  const planCreditsRaw = credits.plan_credits ?? null;
  const legacyBalance = Math.max(
    0,
    toNumber(planCreditsRaw?.balance) ?? toNumber(breakdown.rollover) ?? 0
  );

  // Only what the server states outright. `null` means "not reported", which is
  // different from a reported zero and is filled in from the total below.
  const statedWeekly =
    toNumber(weeklyRaw?.remaining) ??
    toNumber(weeklyRaw?.balance) ??
    toNumber(breakdown.subscription);

  // `purchased` may arrive as an object (new) or a bare number (legacy breakdown).
  const statedPurchased =
    toNumber(
      typeof purchasedRaw === 'object'
        ? purchasedRaw?.remaining ?? purchasedRaw?.balance
        : purchasedRaw
    ) ??
    (breakdown.purchased !== undefined || breakdown.bonus !== undefined
      ? (toNumber(breakdown.purchased) ?? 0) + (toNumber(breakdown.bonus) ?? 0)
      : null);

  let weeklyBalance: number;
  let purchasedBalance: number;

  if (statedWeekly !== null && statedPurchased !== null) {
    weeklyBalance = statedWeekly;
    purchasedBalance = statedPurchased;
    // Safety net: if the buckets don't add up to the total, the difference goes
    // to purchased rather than vanishing. A renamed field upstream should show
    // as an unattributed remainder, never as credits the user appears to have lost.
    const unattributed = totalAvailable - weeklyBalance - purchasedBalance - legacyBalance;
    if (unattributed > 0) purchasedBalance += unattributed;
  } else if (statedWeekly !== null) {
    weeklyBalance = statedWeekly;
    purchasedBalance = totalAvailable - weeklyBalance - legacyBalance;
  } else if (statedPurchased !== null) {
    purchasedBalance = statedPurchased;
    weeklyBalance = totalAvailable - purchasedBalance - legacyBalance;
  } else {
    // Nothing stated: fill the weekly bucket first, since it is spent first.
    weeklyBalance = Math.min(allowance, totalAvailable - legacyBalance);
    purchasedBalance = totalAvailable - legacyBalance - weeklyBalance;
  }

  // Clamped so a legacy balance can never render a >100% weekly progress bar.
  weeklyBalance = Math.min(allowance, Math.max(0, weeklyBalance));
  purchasedBalance = Math.max(0, purchasedBalance);

  const weeklyDate = asDateString(
    weeklyRaw?.resets_at ?? weeklyRaw?.resets_on ?? credits.resets_at ?? credits.resets_on
  );
  const purchasedDate = asDateString(
    (typeof purchasedRaw === 'object'
      ? purchasedRaw?.next_expiry ?? purchasedRaw?.expires_at ?? purchasedRaw?.expires_on
      : null) ??
      credits.expires_at ??
      credits.expires_on
  );

  return {
    weekly: {
      allowance,
      balance: weeklyBalance,
      date: weeklyDate,
      daysRemaining: toNumber(weeklyRaw?.days_until_reset) ?? daysUntil(weeklyDate),
    },
    purchased: {
      balance: purchasedBalance,
      date: purchasedDate,
      daysRemaining:
        toNumber(typeof purchasedRaw === 'object' ? purchasedRaw?.days_until_expiry : null) ??
        daysUntil(purchasedDate),
    },
    legacy: legacyBalance,
    isGrandfathered,
    legacyNote:
      typeof raw?.subscription?.note === 'string' && raw.subscription.note.trim() !== ''
        ? raw.subscription.note
        : null,
    totalAvailable,
    spendableCeiling: Math.max(allowance + purchasedBalance + legacyBalance, totalAvailable, 1),
    usedThisPeriod: Math.max(0, toNumber(credits.used_this_period) ?? 0),
    runningLow: credits.running_low === true,
    isExhausted: totalAvailable <= 0,
    actions: Array.isArray(raw?.actions) ? raw.actions : [],
  };
};

/**
 * Single source of truth for the signed-in account's credit balance.
 *
 * `credits` is `null` until the first response lands, so callers can tell
 * "not loaded yet" apart from "genuinely zero" — showing an out-of-credits
 * state while the balance is still in flight is the classic false-gate bug.
 */
export const useCredits = (enabled = true) => {
  // The weekly grant is a pricing figure, so it comes from the pricing endpoint
  // rather than being assumed here.
  const { pricing } = useCreditPricing();
  const weeklyGrant = pricing.weeklyCredits;

  const query = useQuery({
    queryKey: [...CREDITS_QUERY_KEY, weeklyGrant],
    queryFn: async () => {
      const response = await apiClient.get<any>('/billing/me/');
      const body = response.data;

      // Most accounts return { message, data: { credits, ... } }, but some
      // double-wrap it as { message, data: { status, data: { credits, ... } } }.
      // Unwrap the extra envelope so every consumer reads a real balance
      // instead of silently falling back to 0.
      let payload = body?.data;
      if (payload && payload.credits === undefined && payload.data) {
        payload = payload.data;
      }

      return normalizeCredits(payload, weeklyGrant);
    },
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: true,
    enabled,
  });

  const credits = query.data ?? null;

  const getCostForAction = useCallback(
    (actionKey: string): number => {
      const match = credits?.actions?.find(
        (action) => action.action?.toLowerCase() === actionKey.toLowerCase()
      );
      return match?.credits_per_use ?? ACTION_CREDIT_COSTS[actionKey] ?? 0;
    },
    [credits]
  );

  /**
   * Find a cost by fuzzy action name (e.g. 'compare', 'share'), for call sites
   * that don't have the exact action key. Falls back to `fallbackCost`.
   */
  const getCostByKeyword = useCallback(
    (keyword: string, fallbackCost: number): number => {
      const match = credits?.actions?.find((action) =>
        action.action?.toLowerCase().includes(keyword.toLowerCase())
      );
      return match?.credits_per_use ?? fallbackCost;
    },
    [credits]
  );

  /**
   * Whether the balance covers `cost`. Returns `true` while the balance is
   * still loading — an unknown balance must never block an action; the server
   * remains the authority and will reject a genuinely underfunded request.
   */
  const canAfford = useCallback(
    (cost: number): boolean => (credits ? credits.totalAvailable >= cost : true),
    [credits]
  );

  return useMemo(
    () => ({
      credits,
      isLoading: query.isLoading,
      isFetched: query.isFetched,
      refetch: query.refetch,
      balance: credits?.totalAvailable ?? 0,
      getCostForAction,
      getCostByKeyword,
      canAfford,
    }),
    [credits, query.isLoading, query.isFetched, query.refetch, getCostForAction, getCostByKeyword, canAfford]
  );
};
